import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { StockAnalogLensData } from '@/mocks/stockAnalogMock';
import { buildDailyOutlook } from '@/utils/analogDailyOutlook';
import { formatPct, pctChipClass } from './AnalogLensAccordion';

type Props = {
  lens: StockAnalogLensData;
  referenceDate: string;
  horizonSessions?: number;
};

function CumulativeTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: { label: string; cumulativeMedian: number } }[];
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="font-medium text-gray-900">{row.label}</p>
      <p className={pctChipClass(row.cumulativeMedian)}>
        Cumulative {formatPct(row.cumulativeMedian)}
      </p>
    </div>
  );
}

export function AnalogDailyOutlookTimeline({
  lens,
  referenceDate,
  horizonSessions = 7,
  subjectLabel = 'SPY / ^GSPC',
}: Props & { subjectLabel?: string }) {
  const outlook = useMemo(
    () => buildDailyOutlook(lens.matches, referenceDate, horizonSessions),
    [lens.matches, referenceDate, horizonSessions],
  );

  if (!outlook?.days.length) return null;

  const chartData = outlook.cumulativePath.map((row) => ({
    label: row.projectedDate.slice(5),
    cumulativeMedian: row.cumulativeMedian,
  }));

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
          Session-by-session playbook
        </p>
        <p className="mt-0.5 text-sm text-gray-700">
          What to expect each trading day after the reference close — median path from{' '}
          {lens.matches.length} analogs ({subjectLabel}).
        </p>
      </div>

      {chartData.length > 1 ? (
        <div className="h-28 w-full">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Expected cumulative move from close
          </p>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
              />
              <ReferenceLine y={0} stroke="#e5e7eb" />
              <Tooltip content={<CumulativeTooltip />} />
              <Line
                type="monotone"
                dataKey="cumulativeMedian"
                stroke="#374151"
                strokeWidth={2}
                dot={{ r: 3, fill: '#374151' }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
}
